import React, { useState } from 'react';
import { X, Lock, KeyRound, ShieldCheck } from 'lucide-react';

interface ChangePasswordModalProps {
  onClose: () => void;
  onSubmit: (newPassword: string) => void;
  email: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ onClose, onSubmit, email }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    const passwordRegex = {
      minLength: /.{8,}/,
      upper: /[A-Z]/,
      lower: /[a-z]/,
      digit: /[0-9]/,
      special: /[!@#$%^&*]/,
    };

    if (!passwordRegex.minLength.test(newPassword)) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (!passwordRegex.upper.test(newPassword)) {
      setError('Password must contain at least one uppercase letter.');
      return;
    }
    if (!passwordRegex.lower.test(newPassword)) {
      setError('Password must contain at least one lowercase letter.');
      return;
    }
    if (!passwordRegex.digit.test(newPassword)) {
      setError('Password must contain at least one number.');
      return;
    }
    if (!passwordRegex.special.test(newPassword)) {
      setError('Password must include a special character (!@#$%^&*).');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    onSubmit(newPassword);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="bg-[#20232a] text-white rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.6)] w-full max-w-sm p-6 relative text-xs border border-neutral-700">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-200">
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-yellow-400" />
          Change Password
        </h3>

        <p className="text-xs text-gray-400 mb-4">User: <span className="text-white font-medium">{email}</span></p>

        {/* New Password */}
        <label className="block mb-1 text-gray-300">New Password</label>
        <div className="flex items-center gap-2 bg-neutral-800 rounded px-2 py-2 mb-3">
          <Lock className="w-4 h-4 text-gray-400" />
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password"
            className="bg-transparent text-white text-xs flex-1 outline-none"
          />
        </div>

        {/* Confirm Password */}
        <label className="block mb-1 text-gray-300">Confirm Password</label>
        <div className="flex items-center gap-2 bg-neutral-800 rounded px-2 py-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-gray-400" />
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repeat password"
            className="bg-transparent text-white text-xs flex-1 outline-none"
          />
        </div>

        {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

        <div className="mt-4 text-right">
          <button
            onClick={handleSubmit}
            className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded-md text-white text-xs font-medium shadow-md"
          >
            Update Password
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
